import { Toast } from 'primereact/toast';
import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { ImportTemplates } from '../../constants/appConstants';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './PerformanceAreaComponent.scss';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';

interface PerformanceArea {
  performanceAreaId: number;
  performanceAreaTitle: string;
  performanceAreaDescription: string;
  isActive: boolean;
  providerType: string;
}

const fetchPerformanceAreas = () => {
  return httpService.get(ApiEndpoints.GetAllPerformanceAreasWithInactive);
};

interface PerformanceAreaComponentProps {
  providerTypes: { label: string; value: string }[];
}

const PerformanceAreaComponent: React.FC<PerformanceAreaComponentProps> = ({ providerTypes }) => {
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('performance-area-styles');
    return cleanup;
  }, []);

  const [areas, setAreas] = useState<PerformanceArea[]>([]);
  const { uploadFile } = useDataManagementFileUpload();
  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.PERFORMANCE_AREAS);
      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.PERFORMANCE_AREAS,
      });
      const response = await fetchPerformanceAreas();
      setAreas(response.data as PerformanceArea[]);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance areas uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.performanceAreas,
    onUpload: handleExcelUpload,
    title: 'Import Performance Areas from Excel',
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedArea, setSelectedArea] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<any>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 7;

  const toast = useRef<Toast>(null);

  useEffect(() => {
    setLoading(true);
    fetchPerformanceAreas()
      .then((response: any) => {
        let data = response.data;
        if (!Array.isArray(data)) data = [];
        setAreas(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes: Array.from(new Set(areas.map(a => a.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  let filteredAreas = providerTypeFilter ? areas.filter(a => a.providerType === providerTypeFilter) : areas;
  if (search.trim()) {
    filteredAreas = filteredAreas.filter(a =>
      a.performanceAreaTitle && a.performanceAreaTitle.toLowerCase().includes(search.trim().toLowerCase())
    );
  }

  const totalPages = Math.ceil(filteredAreas.length / rowsPerPage);
  const paginatedAreas = filteredAreas.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading performance areas" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Performance Area" icon="pi pi-plus" onClick={() => {
            setSelectedArea({ performanceAreaId: 0, performanceAreaTitle: '', performanceAreaDescription: '', isActive: true, providerType: '' });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="perf-area-search-anim">
          <input
            type="text"
            placeholder="Search by Title..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="perf-area-provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead>
          <tr style={{ background: '#f0f4f8' }}>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 60 }}>#</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 180 }}>Title</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 250 }}>Description</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 120 }}>Provider Type</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Is Active</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 140 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAreas.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', padding: 16 }}>No performance areas found.</td>
            </tr>
          ) : (
            paginatedAreas.map((area, idx) => (
              <tr key={area.performanceAreaId} style={{ background: idx % 2 === 0 ? '#fff' : '#f6f8fa' }}>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{area.performanceAreaTitle || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{area.performanceAreaDescription || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{area.providerType || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{area.isActive ? 'Yes' : 'No'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>
                  <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} onClick={() => { setSelectedArea({ ...area }); setIsAddMode(false); setEditDialogVisible(true); }} />
                  <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => { setAreaToDelete(area); setDeleteDialogVisible(true); }} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {/* Pagination */}
      <div style={{ marginTop: 16, textAlign: 'right', color: '#888', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc', background: currentPage === 1 ? '#eee' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>&lt;</button>
        <span>Page {currentPage} of {totalPages || 1}</span>
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc', background: (currentPage === totalPages || totalPages === 0) ? '#eee' : '#fff', cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer' }}>&gt;</button>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog
        header={isAddMode ? "Add Performance Area" : `Edit Performance Area${selectedArea && selectedArea.performanceAreaTitle ? ` - ${selectedArea.performanceAreaTitle}` : ''}`}
        visible={editDialogVisible}
        style={{ width: '500px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.performanceAreas, selectedArea);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.performanceAreas, selectedArea);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddPerformanceArea, payload);
                  const response = await fetchPerformanceAreas();
                  setAreas(response.data as PerformanceArea[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance area added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                  // Refresh list after error
                  const response = await fetchPerformanceAreas();
                  setAreas(response.data as PerformanceArea[]);
                } finally {
                  setLoading(false);
                }
              } else if (selectedArea) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdatePerformanceArea}/${selectedArea.performanceAreaId}`, payload);
                  const response = await fetchPerformanceAreas();
                  setAreas(response.data as PerformanceArea[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance area updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Refresh list after error
                  const response = await fetchPerformanceAreas();
                  setAreas(response.data as PerformanceArea[]);
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <PerformanceAreaEditForm editArea={selectedArea} setEditArea={setSelectedArea} providerTypesOptions={providerTypeOptions}/>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={() => {
              setAreas(areas.filter(a => a.performanceAreaId !== (areaToDelete && areaToDelete.performanceAreaId)));
              setDeleteDialogVisible(false);
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{areaToDelete && areaToDelete.title ? `: ${areaToDelete.title}` : ''}?
        </div>
      </Dialog>
    </div>
  );
};

interface PerformanceAreaEditFormProps {
  editArea: any;
  setEditArea: (area: any) => void;
  providerTypesOptions:{
 label: string;
 value: string;
}[];
}

const PerformanceAreaEditForm: React.FC<PerformanceAreaEditFormProps> = ({ editArea, setEditArea,providerTypesOptions }) => {
  return (
    <DataManagementForm
      fields={dataManagementFormConfig.performanceAreas}
      values={editArea}
      onChange={setEditArea}
      options={{ providerType: providerTypesOptions }}
    />
  );
};

export default PerformanceAreaComponent;